import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { forwardRef, memo, type ReactNode, type RefAttributes, useMemo } from 'react'

import {
  toPostHogCaptureAttributes,
  type TrackingAttributes,
} from '../../../code-only/lib/posthog-capture-attributes.ts'
import { borderRadius, shadow, spacing } from '../../../foundations/index.ts'
import { Text, TextContainer } from '../../../foundations/text/index.ts'
import { AvatarContext } from '../../avatar/context.ts'
import { ButtonGroup } from '../../button/button-group.tsx'
import { Button, type ButtonSize, type ButtonStyle, type ButtonVariant } from '../../button/index.ts'

export type ActionButtonItem = {
  label: string
  onClick?: (() => void) | undefined
  isLoading?: boolean | undefined
  disabled?: boolean | undefined
  variant?: ButtonVariant | undefined
  size?: ButtonSize | undefined
  href?: string | undefined
  target?: '_blank' | undefined
  trackingId?: string | undefined
}

type CalloutNotificationTemplateProps = {
  leftAvatar: ReactNode
  leftAvatarSize: '30' | '40'
  rightAvatar?: ReactNode | undefined

  actionButtons?: ActionButtonItem[] | undefined
  buttonStyle?: ButtonStyle | undefined
  trackingAttributes?: TrackingAttributes | undefined
  rightAvatarTrackingId?: string | undefined

  backgroundColor: string
  accentColor: string
  textColor: string
  shadow?: boolean | undefined
  indented?: boolean | undefined

  title?: string | undefined

  onRightAvatarClick?: (() => void) | undefined

  children?: ReactNode | undefined
  className?: string | undefined
  // The 'forwardRef' is mandatory to use 'TemporaryNotification' as a custom component for notistack 'Snackbar'
} & RefAttributes<HTMLDivElement>

export const CalloutNotificationTemplate = memo<CalloutNotificationTemplateProps>(
  forwardRef<HTMLDivElement, CalloutNotificationTemplateProps>(function CalloutNotificationTemplate(props, ref) {
    const {
      leftAvatar,
      leftAvatarSize,
      rightAvatar,
      actionButtons,
      buttonStyle,
      trackingAttributes,
      rightAvatarTrackingId,
      backgroundColor,
      accentColor,
      textColor,
      shadow,
      indented,
      title,
      onRightAvatarClick,
      children,
      className,
    } = props

    const leftAvatarContext = useMemo(
      () => ({ color: accentColor, size: leftAvatarSize }) as const,
      [accentColor, leftAvatarSize],
    )
    const rightAvatarContext = useMemo(() => ({ color: textColor, size: '30' }) as const, [textColor])

    const titleWrapper = title ? (
      <Text
        variant="body2SemiBold"
        css={css`
          overflow-wrap: break-word;
          hyphens: auto;
        `}
      >
        {title}
      </Text>
    ) : undefined

    const childrenWrapper = children ? (
      <TextContainer
        css={css`
          overflow-wrap: ${typeof children === 'string' ? 'break-word' : 'normal'};
          hyphens: ${typeof children === 'string' ? 'auto' : 'none'};
        `}
        variant="body2"
      >
        {typeof children === 'string' ? <Text>{children}</Text> : children}
      </TextContainer>
    ) : undefined

    return (
      <Container
        backgroundColor={backgroundColor}
        textColor={textColor}
        shadow={shadow}
        className={className}
        ref={ref}
        {...toPostHogCaptureAttributes(trackingAttributes)}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          {leftAvatar ? (
            <div
              css={css`
                flex-shrink: 0;
                padding-top: ${!title ? spacing[10] : undefined};
                padding-bottom: ${!title ? spacing[10] : undefined};
                margin-right: ${spacing[40]};
              `}
            >
              <AvatarContext.Provider value={leftAvatarContext}>{leftAvatar}</AvatarContext.Provider>
            </div>
          ) : null}

          <div
            css={css`
              min-width: 0;
              flex-grow: 1;
            `}
          >
            {title ? titleWrapper : childrenWrapper}
          </div>

          {rightAvatar ? (
            <div
              css={css`
                flex-shrink: 0;

                margin-left: ${spacing[40]};

                cursor: pointer;
              `}
              onClick={onRightAvatarClick}
              data-ph-capture-attribute-tracking_id={rightAvatarTrackingId}
            >
              <AvatarContext.Provider value={rightAvatarContext}>{rightAvatar}</AvatarContext.Provider>
            </div>
          ) : null}
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;

            margin-left: ${indented ? '28px' : '0px'};
          `}
        >
          {title && children ? (
            <div
              css={css`
                margin-top: ${spacing[40]};
              `}
            >
              {childrenWrapper}
            </div>
          ) : // when there is no title, the children are in the first row, right to the icon
          null}

          {actionButtons && actionButtons.length > 0 ? (
            <div
              css={css`
                margin-top: ${spacing[40]};
              `}
            >
              <ButtonGroup>
                {actionButtons.map((btn, index) => (
                  <Button
                    key={index}
                    variant={btn.variant ?? 'primary'}
                    style={buttonStyle}
                    size={btn.size ?? 'small'}
                    isLoading={btn.isLoading}
                    disabled={btn.disabled}
                    trackingId={btn.trackingId}
                    data-ph-capture-attribute-tracking_id={btn.trackingId}
                    {...(btn.onClick === undefined ? {} : { onClick: btn.onClick })}
                    {...(btn.href === undefined ? {} : { href: btn.href, target: btn.target })}
                  >
                    {btn.label}
                  </Button>
                ))}
              </ButtonGroup>
            </div>
          ) : null}
        </div>
      </Container>
    )
  }),
)

const Container = styled.div<{ shadow?: boolean | undefined; textColor: string; backgroundColor: string }>`
  display: flex;
  flex-direction: column;

  padding: ${spacing[50]};

  color: ${(props) => props.textColor};

  /* Not background-color: the ai variant passes a gradient, which background-color would drop. */
  background: ${(props) => props.backgroundColor};
  border-radius: ${borderRadius['20']};

  box-shadow: ${(props) => (props.shadow ? shadow.bottom[20] : '')};
`
